// Exact, source-backed recovery terms from the validated INFO-006 launch
// support package. These remain candidate-theme behavior only; no product
// search fields, tags, identifiers, prices, or inventory are changed.
const HAO_SEARCH_RECOVERY_TERMS = new Map([
  ['commercial dish soap', 'Dawn Professional'],
  ['two ply bath tissue', 'bath tissue'],
]);

class HaoPredictiveSearch extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.form = this.querySelector('form');
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');
    this.results = this.querySelector('[data-hao-predictive-search]');
    this.status = this.querySelector('[data-hao-predictive-search-status]');
    this.cache = new Map();
    this.abortController = null;
    this.debounceTimer = null;

    if (!this.form || !this.input || !this.results || !this.status) return;

    this.input.addEventListener('input', () => this.queueSearch());
    this.input.addEventListener('focus', () => this.queueSearch());
    this.input.addEventListener('keydown', (event) => this.onKeydown(event));
    this.addEventListener('focusout', () => {
      window.setTimeout(() => {
        if (!this.contains(document.activeElement)) this.close();
      });
    });
    this.resetButton?.addEventListener('click', () => {
      window.setTimeout(() => this.close(true));
    });
  }

  query() {
    return this.input.value.trim();
  }

  queueSearch() {
    window.clearTimeout(this.debounceTimer);
    const term = this.query();
    if (!term) {
      this.close(true);
      return;
    }
    this.debounceTimer = window.setTimeout(() => this.getResults(term), 200);
  }

  onKeydown(event) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.moveSelection(event.key === 'ArrowDown' ? 1 : -1);
      return;
    }
    if (event.key === 'Enter') {
      const selected = this.querySelector('[aria-selected="true"] a');
      if (selected) {
        event.preventDefault();
        selected.click();
      }
    }
    if (event.key === 'Escape') this.close();
  }

  moveSelection(direction) {
    if (!this.hasAttribute('open')) return;
    const options = [...this.querySelectorAll('.predictive-search__list-item')];
    if (!options.length) return;
    const current = options.findIndex((option) => option.getAttribute('aria-selected') === 'true');
    const next = current < 0 ? (direction > 0 ? 0 : options.length - 1) : (current + direction + options.length) % options.length;
    options.forEach((option, index) => option.setAttribute('aria-selected', String(index === next)));
    this.input.setAttribute('aria-activedescendant', options[next].id);
  }

  async getResults(term) {
    if (this.cache.has(term)) {
      this.render(this.cache.get(term));
      return;
    }
    this.abortController?.abort();
    this.abortController = new AbortController();
    this.setAttribute('loading', '');
    this.status.setAttribute('aria-hidden', 'false');
    this.status.textContent = this.dataset.loadingText || 'Loading suggestions';

    try {
      const requestTerm = HAO_SEARCH_RECOVERY_TERMS.get(term.toLowerCase()) || term;
      const url = new URL(window.routes.predictive_search_url, window.location.origin);
      url.searchParams.set('q', requestTerm);
      url.searchParams.set('section_id', 'hao-predictive-search');
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', '10');
      url.searchParams.set('resources[options][unavailable_products]', 'hide');
      url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor,body');
      const response = await fetch(url, { signal: this.abortController.signal });
      if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);
      const html = await response.text();
      const section = new DOMParser().parseFromString(html, 'text/html').querySelector('#shopify-section-hao-predictive-search');
      if (!section) throw new Error('Predictive search response did not contain the expected section');
      const markup = section.innerHTML;
      this.cache.set(term, markup);
      if (term === this.query()) this.render(markup);
    } catch (error) {
      if (error.name !== 'AbortError') this.close();
    }
  }

  render(markup) {
    this.results.innerHTML = markup;
    const searchButton = this.results.querySelector(
      '.predictive-search__search-for-button button',
    );
    if (searchButton) {
      searchButton.textContent = `Search the catalog for “${this.query()}”`;
    }
    this.removeAttribute('loading');
    this.setAttribute('open', '');
    this.input.setAttribute('aria-expanded', 'true');
    this.status.textContent = this.querySelector('[data-hao-predictive-search-live-count]')?.textContent.trim() || '';
    this.status.setAttribute('aria-hidden', 'false');
  }

  close(clearResults = false) {
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input?.setAttribute('aria-expanded', 'false');
    this.input?.setAttribute('aria-activedescendant', '');
    this.status?.setAttribute('aria-hidden', 'true');
    if (clearResults && this.results) this.results.innerHTML = '';
  }
}

if (!customElements.get('hao-predictive-search')) {
  customElements.define('hao-predictive-search', HaoPredictiveSearch);
}
